import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, RefreshCcw } from "lucide-react";
import ComparePlayer from "@/components/sunovoice/ComparePlayer";
import { CLIP_BUSY, clipBusy, clipSrc, makeVoiceClip } from "@/lib/sunoVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const card = "rounded-3xl border p-5";
const eyebrow = "font-mono text-[10px] uppercase tracking-[0.2em]";

function SunoSide({ voice, group }) {
  const s = voice.sample;
  const [take, setTake] = useState(1);
  const clip = (s?.clips || []).find((c) => c.n === take) || s?.clips?.[0];
  if (clipBusy(voice) && !clip) {
    return <p data-testid="compare-clip-busy" className="flex items-start gap-2 text-[13px] text-white/65"><Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-amber-200" />{CLIP_BUSY[s.status]} Usually 1–3 minutes.</p>;
  }
  if (!clip) return <p data-testid="compare-clip-none" className="text-[13px] text-white/55">{s?.status === "error" ? "The clip couldn't be made." : "No clip yet."}</p>;
  return (
    <div className="space-y-3">
      <ComparePlayer src={clipSrc(clip)} duration={clip.duration} testId="compare-suno" group={group} accent="bg-gradient-to-r from-cyan-300 to-emerald-300" />
      {s.clips.length > 1 ? (
        <div className="flex gap-1.5">
          {s.clips.map((c) => (
            <button key={c.n} type="button" data-testid={`compare-take-${c.n}`} aria-pressed={clip.n === c.n} onClick={() => setTake(c.n)}
              className={`rounded-full border px-3 py-1 text-[12px] transition-colors ${clip.n === c.n ? "border-cyan-300/60 bg-cyan-300/15 text-white" : "border-white/10 text-white/60 hover:bg-white/[0.06]"}`}>Take {c.n}</button>
          ))}
        </div>
      ) : null}
      {clipBusy(voice) ? <p className="flex items-center gap-1.5 text-[11px] text-amber-100/80"><Loader2 className="h-3 w-3 animate-spin" /> Making a new clip…</p> : null}
    </div>
  );
}

/** "Does it sound like you?" — your real singing and the ELPM voice singing the same words, side by side. */
export default function VoiceCompare({ voice, onChanged }) {
  const group = useRef(new Set());
  const [busy, setBusy] = useState(false);
  const s = voice.sample;
  const remake = async () => {
    setBusy(true);
    try { onChanged(await makeVoiceClip(voice.id)); toast.success("Making a new clip", { description: "ELPM sings your words again — about 1–3 minutes." }); }
    catch (e) { toast.error("Couldn't make the clip", { description: safeApiError(e) }); }
    setBusy(false);
  };
  const words = (s?.lyrics || "").replace(/\[[^\]]*\]\n?/g, "").trim();
  return (
    <section data-testid="voice-compare" className="space-y-4">
      <div>
        <p className={`${eyebrow} text-cyan-300/80`}>Voice check</p>
        <h2 className="mt-1 font-display text-base font-semibold md:text-lg">Does it sound like you?</h2>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div data-testid="compare-real" className={`${card} border-white/[0.08] bg-[#0a0d1c]/85`}>
          <p className={`${eyebrow} mb-4 text-white/50`}>Your real singing</p>
          <ComparePlayer src={voice.source_url} start={voice.start} end={voice.end} testId="compare-real" group={group} accent="bg-gradient-to-r from-amber-200 to-rose-300" />
        </div>
        <div data-testid="compare-suno" className={`${card} border-cyan-300/25 bg-cyan-300/[0.04]`}>
          <p className={`${eyebrow} mb-4 text-cyan-200/80`}>Your ELPM voice · full gospel band</p>
          <SunoSide voice={voice} group={group} />
        </div>
      </div>
      {s?.status === "error" ? <p data-testid="compare-clip-error" className="flex items-start gap-2 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-100"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{s.error || "ELPM couldn't make the clip."}</p> : null}
      {words ? (
        <div data-testid="compare-words" className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <p className={`${eyebrow} text-white/45`}>{s.heard === false ? "Sung words (a short gospel line)" : "The words you sang"}</p>
          <p className="mt-2 whitespace-pre-line text-[13px] leading-relaxed text-white/75">{words}</p>
          {s.heard === false ? <p data-testid="compare-words-fallback" className="mt-2 text-[11px] text-amber-200/80">We couldn't make out the words in your recording, so ELPM sings this line instead.</p> : null}
        </div>
      ) : null}
      {!clipBusy(voice) ? (
        <button type="button" data-testid="compare-remake" onClick={remake} disabled={busy} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[13px] hover:bg-white/[0.06] disabled:opacity-40">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />} {s?.clips?.length ? "Make a new clip" : "Make the clip"} <span className="text-white/40">· uses a little ELPM credit</span>
        </button>
      ) : null}
    </section>
  );
}
